import { createFileRoute } from '@tanstack/react-router'
import { AppHeader } from '#/components/layout/app-header'
import { Markdown } from '#/components/common/markdown'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'

export const Route = createFileRoute('/docs')({ component: Docs })

const EN = `
## 1. Plan
Describe the idea. Bystep asks seven clarifying questions, then writes a **PRD** with seven fixed sections, maps it into **features → sub-features**, and breaks every feature into **8–20 tasks**.

Tasks are layered on purpose: every **frontend task is built on mock data first**, and backend tasks (schema → API → realtime) follow. You can click through the UI before a single line of backend exists.

## 2. Hand it to your agent
Press **Start building → Prompt for your AI agent** and paste the prompt into Claude Code, Cursor, Codex, OpenCode or any agent with a terminal.

The prompt logs the agent in with a personal token, binds the repo to your workspace, installs the \`bystep\` skill, and starts the loop:

\`\`\`
npx @bystep/cli task next --plan <id> --json   # the server picks ONE task
npx @bystep/cli task start <ref>
# … work …
npx @bystep/cli task complete <ref>
\`\`\`

The agent never sees the whole backlog — it asks for one task, finishes it, and asks again.

## 3. Checkpoints
Whenever the next task changes **layer** (frontend → backend) or **phase**, the agent stops and reports. You verify in the browser, then say *continue*. That's the whole idea: powerful agent, human decides when to move on.

## 4. Codebase sync (privacy-first)
\`npx @bystep/cli sync --plan\` scans the repo, respects \`.gitignore\`, refuses \`.env\` and secrets, and uploads **only** file paths, sizes, hashes and languages. Your agent writes a short summary per file locally; only summaries are uploaded. From those, Bystep builds a **wiki** and unlocks the next plan in the workspace.

## 5. Revising the plan
Ask the Bystep Agent to change the PRD. Every change creates a **proposal** (added / updated / removed) that you approve or reject — nothing is overwritten silently.
`
const ID = `
## 1. Rencanakan
Ceritakan idemu. Bystep menanyakan tujuh pertanyaan klarifikasi, lalu menulis **PRD** dengan tujuh bagian baku, memetakannya jadi **fitur → sub-fitur**, dan memecah tiap fitur jadi **8–20 task**.

Task sengaja berlapis: semua **task frontend dibangun di atas data tiruan dulu**, baru task backend (skema → API → realtime). Kamu bisa klik-klik UI sebelum satu baris backend pun ada.

## 2. Serahkan ke agent
Tekan **Mulai bangun → Prompt untuk AI agent** dan tempel prompt-nya ke Claude Code, Cursor, Codex, OpenCode, atau agent lain yang punya terminal.

Prompt itu membuat agent login dengan token pribadi, mengikat repo ke workspace, memasang skill \`bystep\`, lalu memulai loop:

\`\`\`
npx @bystep/cli task next --plan <id> --json   # server memilih SATU task
npx @bystep/cli task start <ref>
# … kerjakan …
npx @bystep/cli task complete <ref>
\`\`\`

Agent tidak pernah melihat seluruh backlog — ia minta satu task, menyelesaikannya, lalu minta lagi.

## 3. Checkpoint
Tiap kali task berikutnya ganti **layer** (frontend → backend) atau **fase**, agent berhenti dan melapor. Kamu verifikasi di browser, lalu bilang *lanjut*. Itulah intinya: agent bertenaga, manusia menentukan kapan lanjut.

## 4. Sync codebase (privasi dulu)
\`npx @bystep/cli sync --plan\` memindai repo, menghormati \`.gitignore\`, menolak \`.env\` dan secret, dan hanya mengunggah **path, ukuran, hash, dan bahasa** file. Agent kamu menulis ringkasan singkat per file secara lokal; hanya ringkasannya yang naik. Dari situ Bystep membangun **wiki** dan membuka rencana berikutnya di workspace.

## 5. Merevisi rencana
Minta Bystep Agent mengubah PRD. Setiap perubahan jadi **usulan** (ditambah / diubah / dihapus) yang kamu terapkan atau tolak — tidak ada yang ditimpa diam-diam.
`

function Docs() {
  return (
    <>
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-3xl space-y-4">
          <h1 className="text-3xl font-bold">{m.docs_title()}</h1>
          <p className="text-muted-foreground">{m.docs_intro()}</p>
          <Markdown content={getLocale() === 'id' ? ID : EN} className="prose-base" />
          {/* non-tech F4: hosted MCP connector — linked from /integrations as /docs#mcp-hosted */}
          <section id="mcp-hosted" className="scroll-mt-24">
            <Markdown content={m.mcphosted_docs_md()} className="prose-base" />
          </section>
          {/* Inspirasi F0: agent-guide.md / llms.txt / /api/schema — how any AI assistant can guide a person or drive the CLI */}
          <section id="agent-guide" className="scroll-mt-24">
            <Markdown content={m.guide_docs_md()} className="prose-base" />
          </section>
          {/* Parity P0: build on your own computer with your own subscription (`bystep serve`) */}
          <section id="serve" className="scroll-mt-24">
            <Markdown content={m.serve_docs_md()} className="prose-base" />
          </section>
        </div>
      </main>
    </>
  )
}
